import { z } from 'zod';

/**
 * Contrato das filas.
 *
 * Vive no pacote compartilhado porque quem **enfileira** (api) e quem **consome** (worker)
 * são processos diferentes: sem um contrato único, o payload muda de um lado e o outro
 * descobre em produção. O schema zod é validado nas duas pontas.
 *
 * Nome de fila é string literal — renomear sem mudar aqui quebra o typecheck, que é o
 * objetivo.
 */

/**
 * Nome de fila obedece ao que o pg-boss aceita: letras, números, `_`, `-`, `.` e `/`.
 * **Dois-pontos não entra** — `email:send` derruba o worker na subida, com
 * `AssertionError` do `assertQueueName`, e só aparece em runtime.
 */
export const QUEUE_NAME_PATTERN = /^[A-Za-z0-9_\-./]+$/;

export const QUEUES = {
  emailSend: 'email.send',
  auditRecord: 'audit.record',
  /**
   * Manutenção das partições mensais (ADR-0007 — no lugar do `pg_partman`).
   *
   * ⚠️ O ADR escreve o nome como `partitions:maintain`. **Dois-pontos o pg-boss não aceita**
   * (`assertQueueName` derruba o worker na subida), então aqui é ponto.
   */
  partitionsMaintain: 'partitions.maintain',
  /** Retenção de 30 dias do payload cru dos webhooks. */
  webhookPrune: 'webhook.prune',
  /**
   * Vigilância de divergência (Fase 3): recontagem local × `participants_count`, retrato em
   * `group_snapshots` e incidente `DATA_DIVERGENCE` quando os dois não batem.
   *
   * É uma fila e não um `setInterval` porque duas réplicas do worker não podem medir o
   * mesmo grupo duas vezes — o pg-boss coordena isso pelo banco.
   */
  groupDivergence: 'group.divergence',
  /**
   * Reconstrução + conferência do resumo diário (Fase 6).
   *
   * É a fila que sustenta a regra inviolável 1 de `docs/product/metricas.md`: métrica
   * derivada nunca é gravada como verdade — é rollup reconstruível, **e existe job que
   * prova**. Ele reconstrói a janela recente do fato e, na mesma passada, reconta e compara
   * com o que gravou; diferença abre `ROLLUP_DIVERGENCE`.
   */
  analyticsRollup: 'analytics.rollup',
  /**
   * Sincronização das contas de anúncio da Meta (Fase 8).
   *
   * É fila, e não `setInterval`, pelo motivo de sempre — duas réplicas do worker não podem
   * sincronizar a MESMA conta ao mesmo tempo, e o pg-boss coordena isso pelo banco. Aqui o
   * motivo pesa mais do que nos outros: a cota da Graph API é **por conta de anúncio e
   * compartilhada** com todo mundo que usa o mesmo app (inclusive o cliente mexendo no
   * Gerenciador de Anúncios). Duas rodadas simultâneas gastariam a cota em dobro e
   * bloqueariam a conta pela janela inteira.
   */
  adsSync: 'ads.sync',
  /**
   * Vigilância de monitorador fora do ar (`MONITOR_OFFLINE`).
   *
   * É a única fila cujo produto é descobrir um **silêncio**: um número caído não gera
   * evento nenhum, e "zero entradas" fica indistinguível de "não entrou ninguém". Sem ela,
   * o primeiro a desconfiar de uma queda de madrugada é o cliente, dias depois.
   *
   * É fila e não `setInterval` pelo motivo de sempre — duas réplicas do worker varrendo os
   * mesmos monitoradores ao mesmo tempo passariam juntas pelo mesmo estado; o pg-boss
   * coordena isso pelo banco. Aqui o estrago de duas rodadas simultâneas seria pequeno (o
   * índice parcial de `incidents` segura a duplicata), mas a cadência de 5 minutos é a mais
   * alta do worker e não há motivo para pagá-la em dobro.
   */
  monitorOffline: 'monitor.offline',
  /**
   * O guardião do tráfego pago: convite morto, link sem grupo, folga acabando, grupo sumido.
   *
   * É a única fila que existe para avisar **antes** do prejuízo — as outras contam o que já
   * aconteceu. E é a única que fala com o provider POR GRUPO, o que muda a natureza do
   * problema de concorrência: duas réplicas do worker varrendo juntas não abririam incidente
   * duplicado (o índice parcial de `incidents` segura), mas fariam o DOBRO de chamadas pela
   * mesma sessão de WhatsApp — e sessão que consulta demais é sessão limitada ou banida. Ou
   * seja: aqui o pg-boss não está economizando banco, está protegendo o número do cliente.
   */
  trafficGuard: 'traffic.guard',
  /**
   * A rede de segurança do casamento clique → entrada.
   *
   * O casamento ao vivo acontece na ingestão, quando a entrada chega. Esta fila existe para
   * o que o ao vivo não alcança: entrada gravada antes de o casador existir (todas até
   * 19/09/2026), webhook que ganhou a corrida do clique, e a falha isolada que a ingestão
   * engole de propósito para não perder o fato. Também é quem FECHA a sessão vencida — sem
   * ela, "ainda pode casar" e "não casou" ficariam indistinguíveis para sempre.
   */
  attributionSweep: 'attribution.sweep',
  /**
   * F1 — importação da comissão da Shopee (Affiliate Open API) para `daily_earnings`.
   *
   * Fila, e não `setInterval`, pelo motivo de sempre: duas réplicas do worker importando a
   * MESMA credencial ao mesmo tempo gastariam o limite de chamadas da conta de afiliado em
   * dobro — e a Shopee recusa a rodada inteira quando ele estoura.
   */
  shopeeSync: 'shopee.sync',
} as const;

export type QueueName = (typeof QUEUES)[keyof typeof QUEUES];

export const emailKind = z.enum(['verify-email', 'reset-password', 'workspace-invite', 'generic']);
export type EmailKind = z.infer<typeof emailKind>;

/** E-mail transacional. O corpo já vem renderizado — o worker não conhece templates de produto. */
export const emailSendPayload = z.object({
  /**
   * Linha correspondente em `email_deliveries`. É o que transforma "o job rodou" em
   * "o e-mail chegou ou não chegou, e por quê": o worker faz a trava do efeito nessa
   * linha (`QUEUED → SENDING`) antes de falar com o SMTP, então uma reentrega do
   * pg-boss não vira um segundo e-mail na caixa de quem recebeu.
   */
  deliveryId: z.string().uuid(),
  to: z.string().email(),
  subject: z.string().min(1),
  html: z.string().min(1),
  text: z.string().min(1),
  /** Agrupa tentativas do mesmo propósito para diagnóstico (ex.: 'verify-email'). */
  kind: emailKind,
  /** Correlaciona com o log da requisição que originou o envio. */
  requestId: z.string().optional(),
});
export type EmailSendPayload = z.infer<typeof emailSendPayload>;

/**
 * Auditoria assíncrona. Só para ações de alto volume; ação sensível (revelar telefone,
 * transferir propriedade) grava **na mesma transação**, senão uma falha de fila apaga a trilha.
 */
export const auditRecordPayload = z.object({
  workspaceId: z.string().uuid().nullable(),
  /** Nem toda ação parte de uma pessoa: job, webhook e chave de API também agem. */
  actorType: z.enum(['USER', 'SYSTEM', 'API_KEY', 'WEBHOOK']).default('USER'),
  actorUserId: z.string().uuid().nullable(),
  /** Snapshot do e-mail no instante do fato — sobrevive à exclusão do usuário. */
  actorLabel: z.string().nullable(),
  action: z.string().min(1),
  entityType: z.string().min(1),
  entityId: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  /** Hex do hash do IP; vira `bytea` na gravação. Nunca o IP em claro. */
  ipHash: z
    .string()
    .regex(/^[0-9a-f]+$/i)
    .nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  occurredAt: z.coerce.date(),
});
export type AuditRecordPayload = z.infer<typeof auditRecordPayload>;

/**
 * Job de manutenção não recebe nada: o que ele faz depende do relógio, não de argumento.
 * O schema vazio existe para o registro de filas continuar exaustivo — fila sem payload
 * declarado não compila.
 */
export const maintenancePayload = z.object({}).passthrough();
export type MaintenancePayload = z.infer<typeof maintenancePayload>;

export const QUEUE_PAYLOADS = {
  [QUEUES.emailSend]: emailSendPayload,
  [QUEUES.auditRecord]: auditRecordPayload,
  [QUEUES.partitionsMaintain]: maintenancePayload,
  [QUEUES.webhookPrune]: maintenancePayload,
  [QUEUES.groupDivergence]: maintenancePayload,
  [QUEUES.analyticsRollup]: maintenancePayload,
  /**
   * Sem `connectionId` é a rodada agendada (todas as contas). Com ele é o "Sincronizar
   * agora" de UMA conta — o botão não pode virar rodada de todos os clientes.
   */
  [QUEUES.adsSync]: maintenancePayload.extend({ connectionId: z.string().uuid().optional() }),
  [QUEUES.monitorOffline]: maintenancePayload,
  [QUEUES.trafficGuard]: maintenancePayload,
  [QUEUES.attributionSweep]: maintenancePayload,
  /** "Importar agora" manda o id de UMA conexão; a rodada agendada passa por todas. */
  [QUEUES.shopeeSync]: maintenancePayload.extend({ connectionId: z.string().uuid().optional() }),
} as const;

/**
 * Política de cada fila — **uma definição só**, porque quem cria a fila e quem publica
 * nela são processos diferentes. A mesma regra escrita em dois lugares já derrubou o
 * worker em produção uma vez (`MAIL_FROM` validado na api e no worker; corrigi um lado só).
 *
 * `retentionSeconds` é por quanto tempo o job continua no banco depois de terminar: é a
 * janela em que ainda dá para responder "por que este e-mail não chegou na terça?".
 */
export type QueuePolicy = {
  retryLimit: number;
  retryBackoff: boolean;
  retentionSeconds: number;
};

const SEVEN_DAYS = 7 * 24 * 60 * 60;

export const QUEUE_POLICIES: Record<QueueName, QueuePolicy> = {
  /**
   * 5 tentativas com backoff. Vale para insucesso **transitório** — SMTP fora do ar,
   * timeout, 4xx. Recusa permanente (550, endereço inexistente) não chega a consumir
   * tentativa: o worker a reconhece, grava `FAILED_PERMANENT` e encerra o job.
   */
  [QUEUES.emailSend]: { retryLimit: 5, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  [QUEUES.auditRecord]: { retryLimit: 5, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /**
   * Manutenção retenta pouco de propósito: ela roda todo dia de qualquer jeito, e insistir
   * numa falha de hoje só atrapalha a execução de amanhã. Falhou, o alerta é o caminho.
   */
  [QUEUES.partitionsMaintain]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  [QUEUES.webhookPrune]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  [QUEUES.groupDivergence]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  [QUEUES.analyticsRollup]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /**
   * Retenta pouco de propósito, e por um motivo que as outras filas não têm: cada tentativa
   * gasta cota da conta de anúncio do CLIENTE. Insistir numa falha de agora é queimar a
   * janela de agora — e a rodada seguinte, daqui a uma hora, refaz tudo de qualquer jeito
   * (o sync é idempotente por `UNIQUE`). Token morto nem chega a consumir tentativa: vira
   * incidente `META_TOKEN_EXPIRED` e a rodada termina com desfecho, não com exceção.
   */
  [QUEUES.adsSync]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /**
   * Retenta pouco, e por um motivo próprio: a rodada seguinte vem em 5 minutos e refaz a
   * varredura inteira do zero (ela é idempotente pelo índice parcial de `incidents`).
   * Insistir numa falha de agora só atrasaria a rodada que já ia consertar o atraso.
   */
  [QUEUES.monitorOffline]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /**
   * Retenta pouco, e aqui o motivo tem preço: cada tentativa refaz consultas ao WhatsApp pela
   * sessão do cliente. Insistir numa falha de agora gasta chamada em cima de um provider que
   * já não está respondendo — e a rodada seguinte, em 15 minutos, refaz tudo do zero (a
   * varredura é idempotente pelo índice parcial de `incidents` e pelo `ON CONFLICT` de
   * `group_link_checks`).
   */
  [QUEUES.trafficGuard]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /**
   * Retenta pouco: a varredura é idempotente e a rodada seguinte, 10 minutos depois, refaz
   * tudo do zero. Insistir agora só disputaria banco com quem está tentando gravar entrada.
   */
  [QUEUES.attributionSweep]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
  /** A rodada seguinte, em 1 h, relê a janela inteira (30 dias); retentar muito só gasta cota. */
  [QUEUES.shopeeSync]: { retryLimit: 2, retryBackoff: true, retentionSeconds: SEVEN_DAYS },
};

/**
 * Filas que rodam por agendamento, não por publicação.
 *
 * `partitions.maintain` roda às 03:10 UTC (00:10 em São Paulo) — de madrugada, antes de a
 * virada do mês importar, e fora do horário em que os grupos se mexem. `group.divergence`
 * às 03:40 e `webhook.prune` às 04:10, para as três não disputarem o mesmo banco.
 */
export const QUEUE_SCHEDULES: Partial<Record<QueueName, string>> = {
  [QUEUES.partitionsMaintain]: '10 3 * * *',
  /**
   * 03:40 UTC — depois das partições (03:10) e antes da retenção (04:10). As três são
   * varreduras de tabela grande e disputariam o mesmo banco se rodassem juntas.
   */
  [QUEUES.groupDivergence]: '40 3 * * *',
  [QUEUES.webhookPrune]: '10 4 * * *',
  /**
   * 04:40 UTC — a última da fila da madrugada. Depois da retenção (04:10) de propósito: o
   * `webhook.prune` apaga payload cru, que não é fonte de métrica nenhuma, mas as duas
   * varrem tabela grande e disputariam o mesmo banco se rodassem juntas.
   */
  [QUEUES.analyticsRollup]: '40 4 * * *',
  /**
   * De hora em hora, aos 20 minutos — **não** junto com a fila da madrugada.
   *
   * As outras rodam de madrugada porque varrem tabela grande do nosso banco. Esta é o
   * oposto: ela existe para o número do painel não ficar velho durante o DIA, que é quando
   * alguém está olhando e decidindo quanto gastar. A cadência de 1h está dentro do "a cada
   * 1–4h por ad account ativa" que `docs/integrations/meta-ads-e-atribuicao.md` recomenda,
   * e cada rodada busca só D-0..D-4 — janela pequena, custo de cota previsível.
   *
   * **06/10: de 1h para 10 min**, pedido do dono ("tive que apertar sincronizar para ver o
   * gasto atualizado"). 10 min e não 1 min: a própria Meta só recalcula o gasto do dia a
   * cada ~15–20 min, então ler mais rápido que isso gasta cota da conta do cliente sem
   * trazer número novo. ~6 chamadas por rodada → ~36/h por conta, longe do teto.
   */
  [QUEUES.adsSync]: '*/10 * * * *',
  /**
   * **De 5 em 5 minutos, o dia inteiro** — a cadência mais alta do worker, e a única que
   * não se encaixa na fila da madrugada.
   *
   * As outras rodam às 03h/04h porque varrem tabela grande. Esta é o oposto em tudo: é uma
   * consulta em `whatsapp_monitors` (dezenas de linhas, não milhões), não fala com provider
   * nenhum, e existe justamente para pegar a queda **de madrugada** — agendá-la para um
   * horário fixo seria transformar o problema que ela resolve na forma como ela falha.
   *
   * Por que 5 e não 15: a espera real até alguém ser avisado é `tolerância + cadência`. Com
   * a tolerância de 10 minutos escolhida em `monitor-offline.ts`, 5 minutos de cadência dão
   * 10–15 minutos de cegueira; 15 de cadência dariam até 25. O custo de rodar 288 vezes por
   * dia uma consulta que não sai do banco é ruído perto disso.
   */
  [QUEUES.monitorOffline]: '*/5 * * * *',
  /**
   * **De 10 em 10 minutos, o dia inteiro, nos minutos terminados em 7.**
   *
   * A cadência não define quando a atribuição acontece — o caminho normal é ao vivo, na
   * ingestão, no mesmo segundo da entrada. Aqui é rede de segurança, e o que ela precisa é
   * ser mais curta que a paciência de quem olha o painel: 10 minutos garantem que uma
   * entrada que escapou apareça atribuída antes de alguém desconfiar do número.
   *
   * `7,17,27…` porque `monitor.offline` roda nos múltiplos de 5, `traffic.guard` em
   * `3,18,33,48` e `ads.sync` aos 20: três varreduras no mesmo minuto disputariam o banco.
   */
  [QUEUES.attributionSweep]: '7,17,27,37,47,57 * * * *',
  /**
   * **De 15 em 15 minutos, o dia inteiro, em minutos fora da grade dos outros.**
   *
   * 15 porque é a cadência da faixa mais rápida da sonda (`TRAFFIC_GUARD_HOT_INTERVAL_MS`):
   * agendar o job mais devagar do que isso faria a faixa quente virar enfeite — o intervalo
   * real de conferência é `max(cadência do job, cadência da faixa)`. Mais rápido do que 15
   * não compraria nada: os grupos mornos continuariam vencendo a cada 2h, e a rodada extra só
   * gastaria consulta.
   *
   * `3,18,33,48` e não os quartos de hora redondos: `monitor.offline` roda nos múltiplos de
   * 5 e `ads.sync` aos 20.
   * Três varreduras caindo no mesmo minuto disputariam o mesmo banco — e, pior, a rodada que
   * mais demora (esta, que espera rede) seguraria as outras.
   */
  [QUEUES.trafficGuard]: '3,18,33,48 * * * *',
  /**
   * F1 — de hora em hora, aos 26 minutos (fora da grade dos múltiplos de 5, dos terminados em
   * 7 e de `3,18,33,48`). A Shopee atualiza o relatório de conversões com atraso de minutos a
   * horas, e o total só é escrito para dias FECHADOS — ler mais rápido que 1 h não traria
   * número novo e gastaria o limite de chamadas da conta de afiliado.
   */
  [QUEUES.shopeeSync]: '26 * * * *',
};
